import axios, { AxiosRequestConfig } from 'axios';
import { logout, isSessionInvalidError } from '../logoutUtil';

interface ServiceUtilResponse {
  data: any;
  error: any;
  status?: number;
}

const getDefaultHeaders = () => {
  let accessToken = '';

  if (typeof window !== 'undefined') {
    accessToken = localStorage.getItem('accessToken') || '';
  }

  if (!accessToken && typeof document !== 'undefined') {
    const cookieValue = document.cookie
      .split('; ')
      .find((row) => row.startsWith('accessToken='))
      ?.split('=')[1];
    accessToken = cookieValue ? decodeURIComponent(cookieValue) : '';
  }

  if (!accessToken) {
    return {};
  }

  return {
    Authorization: `Bearer ${accessToken}`,
  };
};

const serviceUtil = {
  get: async (url: string, params: any, headers?: AxiosRequestConfig['headers'], isAuth: boolean = false) => {
    try {
      const mergedHeaders = !isAuth ? { ...getDefaultHeaders(), ...headers } : headers;

      const response = await axios.get(url, {
        params,
        headers: mergedHeaders,
      });
      return {
        data: response.data,
        error: null,
        status: response.status,
      } as ServiceUtilResponse;
    } catch (error: any) {
      console.error('GET request failed', error?.response?.status ?? 'unknown');
      if (isSessionInvalidError(error)) {
        logout();
      }
      return { data: null, error } as ServiceUtilResponse;
    }
  },
  post: async (url: string, data: any, headers?: AxiosRequestConfig['headers'], isAuth: boolean = false) => {
    try {
      const mergedHeaders = !isAuth ? { ...getDefaultHeaders(), ...headers } : headers;

      const response = await axios.post(url, data, {
        headers: mergedHeaders,
      });
      return {
        data: response.data,
        error: null,
        status: response.status,
      } as ServiceUtilResponse;
    } catch (error: any) {
      console.error('POST request failed', error?.response?.status ?? 'unknown');
      if (isSessionInvalidError(error)) {
        logout();
      }
      return { data: null, error } as ServiceUtilResponse;
    }
  },
  put: async (url: string, data: any, headers?: AxiosRequestConfig['headers'], isAuth: boolean = false) => {
    try {
      // Merge default headers with provided headers if isAuth is false
      const mergedHeaders = !isAuth ? { ...getDefaultHeaders(), ...headers } : headers;

      const response = await axios.put(url, data, {
        headers: mergedHeaders,
      });
      return {
        data: response.data,
        error: null,
        status: response.status,
      } as ServiceUtilResponse;
    } catch (error: any) {
      console.error('PUT request failed', error?.response?.status ?? 'unknown');
      if (isSessionInvalidError(error)) {
        logout();
      }
      return { data: null, error } as ServiceUtilResponse;
    }
  },
  delete: async (url: string, headers?: AxiosRequestConfig['headers'], isAuth: boolean = false) => {
    try {
      // Merge default headers with provided headers if isAuth is false
      const mergedHeaders = !isAuth ? { ...getDefaultHeaders(), ...headers } : headers;

      const response = await axios.delete(url, {
        headers: mergedHeaders,
      });
      return {
        data: response.data,
        error: null,
        status: response.status,
      } as ServiceUtilResponse;
    } catch (error: any) {
      console.error('DELETE request failed', error?.response?.status ?? 'unknown');
      if (isSessionInvalidError(error)) {
        logout();
      }
      return { data: null, error } as ServiceUtilResponse;
    }
  },

  patch: async (url: string, data: any, headers?: AxiosRequestConfig['headers'], isAuth: boolean = false) => {
    try {
      // Merge default headers with provided headers if isAuth is false
      const mergedHeaders = !isAuth ? { ...getDefaultHeaders(), ...headers } : headers;

      const response = await axios.patch(url, data, {
        headers: mergedHeaders,
      });
      return {
        data: response.data,
        error: null,
        status: response.status,
      } as ServiceUtilResponse;
    } catch (error: any) {
      console.error('PATCH request failed', error?.response?.status ?? 'unknown');
      if (isSessionInvalidError(error)) {
        logout();
      }
      return { data: null, error } as ServiceUtilResponse;
    }
  },
};

export default serviceUtil;
